import classNames from "classnames";
import YellowAlert from "@ugg/shared/components/common/YellowAlert";
import { getTierColor, getLuTierColor, getTierListPickRateCutoff, getLuTier } from "@ugg/shared/utils/tier-helpers";
import { useTranslation } from "react-i18next";
import { round, numberWithCommas } from "@ugg/shared/utils/math";
import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { RankingStatsInfo } from "@ugg/shared/api/data-parser/champions/ranking-stats";

interface ChampionRankingStatsPersonalizedProps {
  className?: string;
  colClassName?: string;
  rankingStats: RankingStatsInfo;
  queueType: string;
}

export function ChampionRankingStatsPersonalized(props: ChampionRankingStatsPersonalizedProps) {
  const { t } = useTranslation();
  const { className, rankingStats, queueType } = props;

  const { roleMatches, rank, total_rank, win_rate, pick_rate, ban_rate, stdevs } = rankingStats || {};

  const tierLetter = isNaN(stdevs) || !rank ? "?" : getLuTier(stdevs);
  const tierColor = getLuTierColor(stdevs);
  const championMatches = !isNaN(roleMatches) ? numberWithCommas(roleMatches) : "?";
  const winRate = !isNaN(win_rate) ? round(win_rate, 2) : "?";
  const winRateColor = typeof winRate === "number" && !isNaN(winRate) ? getTierColor(winRate) : "";
  const overallRank = rank && total_rank ? `${rank} / ${total_rank}` : "? / ?";
  const pickRate = !isNaN(pick_rate) && pick_rate !== null ? round(pick_rate, 1) : "?";
  const banRate = !isNaN(ban_rate) && ban_rate !== null ? round(ban_rate, 1) : "?";

  let showWarning = pickRate === "?" || pickRate < getTierListPickRateCutoff() || Number(championMatches) < 100;
  if (queueType === QueueTypeS.ONE_FOR_ALL) {
    showWarning = false;
  }

  const valueClassName = "text-[14px] font-extrabold";
  const labelClassName = "text-[10px] font-medium text-accent-gray-100";
  const colClassName = classNames(
    "relative flex flex-col items-center justify-center whitespace-nowrap py-3",
    "after:absolute after:right-0 after:top-1/2 after:-translate-y-1/2 after:w-[1px] after:h-[29px] after:bg-[#383864]",
    "last:after:hidden max-[500px]:[&:nth-child(3n)]:after:hidden",
    props.colClassName,
  );

  return (
    <div
      className={classNames(
        `grid grid-cols-6
        max-[500px]:grid-cols-3 max-[500px]:grid-rows-2
        rounded-l-[3px] max-[1050px]:rounded-[3px] bg-purple-200 overflow-hidden`,
        className,
      )}
    >
      <div className={colClassName}>
        <div className={labelClassName}>Tier</div>
        <div className={classNames(valueClassName, tierColor)}>{tierLetter}</div>
      </div>
      <div className={colClassName}>
        <div className={classNames(labelClassName, "flex items-center")}>
          {t("Win Rate")}
          {showWarning && <YellowAlert className="ml-[3px]" label="Low sample size" />}
        </div>
        <div className={classNames(valueClassName, winRateColor)}>{`${winRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Rank")}</div>
        <div className={valueClassName}>{overallRank}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Pick Rate")}</div>
        <div className={valueClassName}>{pickRate !== "?" && pickRate < 0.1 ? "< 0.1%" : `${pickRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Ban Rate")}</div>
        <div className={valueClassName}>{banRate !== "?" && banRate < 0.1 ? "-" : `${banRate}%`}</div>
      </div>
      <div className={colClassName}>
        <div className={labelClassName}>{t("Matches")}</div>
        <div className={valueClassName}>{championMatches}</div>
      </div>
    </div>
  );
}
